{
  "nav": {
    "application": "Application",
    "organization": "Organization",
    "dashboard": "Dashboard",
    "requests": "Requests",
    "users": "Users",
    "models": "Models",
    "settings": "Settings"
  },
  "userMenu": {
    "settings": "Settings",
    "signOut": "Sign out"
  },
  "theme": {
    "change": "Change theme",
    "light": "Light",
    "dark": "Dark",
    "system": "System"
  },
  "research": {
    "title": "Research demo — not a medical device",
    "body": "Every output on this page is research-only, uses uncalibrated model signals, and requires review by a qualified radiologist. Nothing here is a diagnosis.",
    "demoBody": "These are deterministic simulated results from public test labels; no medical model or model API was called. They are not diagnostic output and require qualified human review.",
    "liveDemoBody": "Live model research results from public, de-identified samples. Scores are ranking signals, not disease probabilities. Qualified human review is required."
  },
  "status": {
    "uploaded": "Uploaded",
    "validating": "Validating",
    "rejected": "Rejected",
    "accepted": "Accepted",
    "readers_queued": "Readers queued",
    "readers_running": "Readers running",
    "reconciling": "Reconciling",
    "abstained": "Abstained",
    "evidence_ready": "Evidence ready",
    "reviewed": "Reviewed"
  },
  "states": {
    "present": "Present",
    "presentWithSeverity": "Present ({{severity}})",
    "uncertain": "Uncertain",
    "absent": "Absent",
    "not_assessed": "Not assessed",
    "yes": "yes",
    "no": "no"
  },
  "severity": {
    "mild": "mild",
    "moderate": "moderate",
    "severe": "severe"
  },
  "auth": {
    "welcome": "Welcome",
    "subtitle": "Sign in to your account or create a new one.",
    "demoSubtitle": "Sign in with the shared demo account. Registration is disabled.",
    "signIn": "Sign in",
    "signUp": "Sign up",
    "email": "Email",
    "password": "Password",
    "signingIn": "Signing in…",
    "inviteCode": "Invite code",
    "inviteHint": "Sign up is invite-only. Ask an admin for a code.",
    "passwordHint": "At least 12 characters.",
    "confirmPassword": "Confirm password",
    "createAccount": "Create account",
    "creatingAccount": "Creating account…"
  },
  "dashboard": {
    "activeSessions": "Active sessions",
    "memberSince": "Member since",
    "accountTitle": "Your account",
    "accountSubtitle": "Only you can see this — every user gets their own content.",
    "email": "Email",
    "role": "Role",
    "emailVerified": "Email verified",
    "verified": "Verified",
    "notVerified": "Not verified"
  },
  "settings": {
    "title": "Settings",
    "language": {
      "title": "Language",
      "subtitle": "Interface language for this account.",
      "en": "English",
      "ru": "Русский"
    },
    "emailTitle": "Email address",
    "emailSubtitle": "Changing your email requires re-verification.",
    "currentPassword": "Current password",
    "updateEmail": "Update email",
    "passwordTitle": "Password",
    "passwordSubtitle": "Changing your password signs out your other sessions.",
    "newPassword": "New password",
    "confirmNewPassword": "Confirm new password",
    "updatePassword": "Update password",
    "sessionsTitle": "Your sessions",
    "sessionsSubtitle": "Devices currently signed in to your account.",
    "device": "Device",
    "ipAddress": "IP address",
    "signedIn": "Signed in",
    "actions": "Actions",
    "thisDevice": "This device",
    "revoke": "Revoke",
    "unknownDevice": "Unknown device"
  },
  "common": {
    "roleAdmin": "Admin",
    "roleMember": "Member",
    "delete": "Delete",
    "actions": "Actions",
    "yes": "Yes",
    "no": "No",
    "never": "Never",
    "search": "Search"
  },
  "requests": {
    "title": "Analysis requests",
    "subtitle": "Chest X-ray analysis requests routed through the evidence pipeline.",
    "new": "New request",
    "searchPlaceholder": "Search by name or request ID",
    "allStatuses": "All statuses",
    "allPatients": "All patients",
    "colRequest": "Request",
    "colName": "Name",
    "colPatient": "Patient",
    "colStatus": "Status",
    "colSource": "Source",
    "colCreatedBy": "Created by",
    "colCreated": "Created",
    "empty": "No requests yet. Create the first one.",
    "upload": "Upload",
    "colResults": "Model runs",
    "recordedCount": "{{count}} recorded",
    "open": "Open results"
  },
  "newRequest": {
    "title": "New analysis request",
    "subtitle": "One de-identified adult frontal chest radiograph per request. Unsupported input is rejected before any model call.",
    "name": "Request name",
    "namePlaceholder": "e.g. Ward B follow-up",
    "patient": "Patient (optional linking)",
    "noPatient": "No patient",
    "existingPlaceholder": "Existing",
    "newCodePlaceholder": "or new code, e.g. case-042",
    "patientHint": "Research linking code only — never a real patient identifier.",
    "imageSource": "Image source",
    "datasetTab": "Dataset",
    "uploadTab": "Upload CXR",
    "datasetHint": "Select one sample",
    "datasetHint2": "— tracked public research fixtures. Checksums are verified before use; policy gates are asserted from the manifest.",
    "demoDatasetHint": "Choose a public sample to open deterministic simulated model results immediately. No model API is called.",
    "liveDemoDatasetHint": "Choose a public sample. Models load on demand; the first result can take a few minutes.",
    "demoOpening": "Opening results…",
    "colSample": "Sample",
    "colView": "View",
    "colAge": "Age",
    "colSex": "Sex",
    "colLabel": "Reference label",
    "fileLabel": "Chest radiograph (DICOM, PNG, or JPEG)",
    "fileHint": "Contents are inspected server-side — extensions and MIME types are not trusted.",
    "view": "View",
    "policyTitle": "Required policy assertions",
    "policyHint": "False or missing assertions stop the request before any model is invoked.",
    "create": "Create request",
    "creating": "Creating…",
    "selectSampleHint": "Select a dataset sample to continue.",
    "attachFileHint": "Attach a file to continue.",
    "setLabel": "Dataset",
    "sets": {
      "testing-set-1": "testing-set-1 — CheXpert (broad findings)",
      "testing-set-3": "testing-set-3 — Shenzhen (pulmonary TB)"
    },
    "primaryLabels": {
      "positive": "TB-compatible appearance (reference)",
      "negative": "Normal CXR (dataset reference)"
    },
    "viewNames": {
      "PA": "PA — posteroanterior",
      "AP": "AP — anteroposterior"
    },
    "viewHints": {
      "PA": "Standard erect chest film: the patient faces the detector and the beam passes from back to front. Preferred when the patient can stand — the heart sits close to the detector, so its shadow is not exaggerated.",
      "AP": "Beam passes from front to back, usually a supine or portable film taken when the patient cannot stand. The heart is farther from the detector, so it looks larger — read cardiac size with care."
    },
    "demoUploadTab": "Upload demo image",
    "demoUploadTitle": "Your desktop demo kit",
    "demoUploadHint": "Choose one image from MedAI Demo Kit / 01_upload. Use the original file; screenshots and edited copies are not accepted.",
    "demoUploadFile": "Public demo image (PNG or JPEG)",
    "demoUploadMatched": "Age and view are matched to the approved public source automatically. Research output requires human review."
  },
  "policy": {
    "adult_verified": "The study is from an adult (18+)",
    "single_frontal_image": "Exactly one frontal PA/AP image",
    "cxr_modality": "This is a chest radiograph (CXR/CR/DX)",
    "no_burned_in_identifiers": "No burned-in patient identifiers are visible"
  },
  "policyShort": {
    "adult_verified": "Adult verified",
    "single_frontal_image": "Single frontal PA/AP image",
    "cxr_modality": "CXR modality",
    "no_burned_in_identifiers": "No burned-in identifiers"
  },
  "show": {
    "createdBy": "created by",
    "patientLabel": "patient",
    "tabIntake": "Intake & validation",
    "tabAnalysis": "Analysis",
    "tabFeedback": "Feedback",
    "retry": "Retry pipeline",
    "noPatient": "no patient",
    "selectPatient": "Select patient",
    "orNewCode": "or new code",
    "linkPatient": "Link patient",
    "intakeTitle": "1 · Intake & validation",
    "intakeSubtitle": "Deterministic gate — contents inspected, policy assertions required before any model call.",
    "sourceDataset": "Source: dataset {{sample}}",
    "sourceUpload": "Source: upload",
    "viewBadge": "View: {{view}}",
    "rejectedTitle": "Rejected before any model call",
    "deidentTitle": "2 · De-identification",
    "deidentSubtitle": "All metadata read from the source file is shown below, then stripped: models only ever receive the metadata-free derivative.",
    "colField": "Source field",
    "colValue": "Value",
    "noMetadata": "No metadata extracted yet.",
    "originalSha": "Original SHA-256:",
    "derivativeSha": "Derivative SHA-256:",
    "derivativeCaption": "Metadata-free PNG derivative (what the models see).",
    "analysisTitle": "3 · Analysis",
    "analysisSubtitle": "Core readers run independently; evidence is reconciled by deterministic application code. MedSigLIP values are uncalibrated ranking scores — not disease probabilities.",
    "running": "Models are generating results ({{status}})… columns fill in as each model finishes.",
    "colFinding": "Finding",
    "colVerdict": "Research selection",
    "descUncalibrated": "ranking score · uncalibrated",
    "descReaderState": "reader state",
    "descChallengerScore": "challenger · score",
    "descChallengerTb": "challenger · TB only",
    "descChallengerState": "challenger · state",
    "verdictAccepted": "Selected for review",
    "verdictDisagreement": "Disagreement",
    "footnote": "Not assessed means the model supplied no result for that finding; a dash in the reconciliation column means no finding was selected. Scores from different model families are never averaged. Reconciliation policy: {{version}}. The demonstration rule has not been clinically validated.",
    "abstainedTitle": "Abstained — no automated evidence produced",
    "provenanceToggle": "Model runs & provenance ({{count}})",
    "provStatus": "status",
    "provAttempts": "attempts",
    "provRevision": "revision",
    "provAdapter": "adapter",
    "provPrompts": "prompts",
    "provPreprocessing": "preprocessing",
    "provTiming": "timing",
    "provWarnings": "warnings",
    "feedbackTitle": "4 · Reviewer feedback",
    "feedbackSubtitle": "Human review is the final, required step: a qualified reviewer will confirm or correct the reconciled evidence here.",
    "feedbackPlaceholder": "Not built yet — coming as the next milestone.",
    "print": "Print report",
    "retryModel": "retry",
    "embeddingSummary": "{{dimension}}-d embedding · {{vectors}} vector(s)",
    "embeddingNone": "no embedding returned",
    "verdictChallengerSignal": "challenger signal",
    "statusNoOutput": "ran but reported no findings",
    "statusAbstained": "abstained — model declined to answer"
  },
  "summary": {
    "title": "Evidence summary",
    "partialBadge": "partial data",
    "stale": "Evidence or safety status changed. Regenerate this summary before using it.",
    "regenerate": "Regenerate with updated data",
    "generateNow": "Generate summary now",
    "partialHint": "Some models are still running — this will be a partial summary.",
    "waiting": "Waiting for the first model results…",
    "generatedAuto": "generated automatically",
    "generatedBy": "generated by {{email}}",
    "attribution": "Composed deterministically from structured evidence; not a generated radiology report.",
    "previousOne": "1 previous version",
    "previousMany": "{{count}} previous versions",
    "partialLabel": "partial",
    "automatic": "automatic",
    "lead": "The study shows findings compatible with:",
    "leadNone": "No accepted positive finding; this does not exclude disease.",
    "uncertainTitle": "Uncertain",
    "disagreementTitle": "Model disagreement",
    "disclaimer": "Research output only — requires review by a qualified radiologist.",
    "scoreLabel": "MedSigLIP {{score}}",
    "history": "Version history ({{count}})",
    "current": "current",
    "findingsSentence": "The study shows findings compatible with: {{findings}}.",
    "uncertainSentence": "Uncertain: {{findings}}.",
    "disagreementSentence": "Model disagreement: {{findings}}.",
    "unavailable": "No summary is available. Check the request state and individual model results."
  },
  "adminPanel": {
    "invitesTitle": "Invite codes",
    "invitesSubtitle": "Sign up is invite-only. Generate and share codes from here.",
    "maxUses": "Max uses",
    "expiresDays": "Expires in (days)",
    "expiresNever": "never",
    "generate": "Generate code",
    "colCode": "Code",
    "colStatus": "Status",
    "colUses": "Uses",
    "colExpires": "Expires",
    "colUsedBy": "Used by",
    "statusActive": "Active",
    "statusRevoked": "Revoked",
    "statusExhausted": "Exhausted",
    "statusExpired": "Expired",
    "revoke": "Revoke",
    "activate": "Activate",
    "copy": "Copy {{code}}",
    "emptyInvites": "No invite codes yet. Generate one above.",
    "usersTitle": "Users",
    "usersSubtitle": "Manage roles and remove accounts.",
    "colEmail": "Email",
    "colRole": "Role",
    "colInvite": "Invite",
    "colVerified": "Verified",
    "colJoined": "Joined",
    "makeAdmin": "Make admin",
    "makeMember": "Make member",
    "you": "You",
    "deleteConfirm": "Delete {{email}}? This cannot be undone."
  },
  "modelsPage": {
    "keyMissing": "RUNPOD_API_KEY is not configured — live endpoint health is unavailable. Health checks are read-only and never start billed workers.",
    "title": "Model fleet",
    "subtitle": "From the checked-in model registry. Adapter mode:",
    "liveCalls": "live inference calls:",
    "enabled": "ENABLED",
    "disabled": "disabled",
    "healthChecked": "health checked",
    "healthNote": "Health = Runpod control plane (read-only, never wakes a worker). Worker-level canonical /health requires scaling a billed worker from zero and stays behind the live-calls gate. Pod APIs are loopback-only; all pods currently stopped.",
    "refresh": "Refresh health",
    "colModel": "Model",
    "colPipeline": "Pipeline",
    "colHealth": "Health",
    "colDeployment": "Deployment",
    "colOperations": "Operations",
    "colAdapter": "Adapter",
    "pipelineCore": "Core",
    "pipelineDisabled": "Disabled",
    "pipelineChallenger": "Challenger",
    "pipelineExcluded": "Excluded",
    "pipelineNotIntegrated": "Not integrated",
    "podOnly": "Pod-only",
    "podQualifiedStopped": "Pod qualified · stopped",
    "notDeployed": "not deployed",
    "keyNotConfigured": "key not configured",
    "unreachable": "unreachable",
    "running": "{{count}} running",
    "ready": "{{count}} ready",
    "scaledToZero": "scaled to zero",
    "throttled": "throttled",
    "unhealthy": "{{count}} unhealthy",
    "queue": "q:{{count}}",
    "sortTitle": "Sort by deployment (serverless → pod-only → not deployed)",
    "routed": "in use",
    "fleetResident": "H200 fleet · warm",
    "fleetDisabled": "H200 fleet · disabled",
    "fleetOnDemand": "H200 · on demand",
    "observed": {
      "ready": "ready",
      "busy": "busy",
      "loading": "loading",
      "unloaded": "unloaded",
      "failed": "failed",
      "unknown": "unknown"
    }
  },
  "report": {
    "title": "MedAI — Research Analysis Report",
    "request": "Request",
    "patient": "Patient",
    "author": "Author",
    "date": "Created",
    "source": "Source",
    "view": "View",
    "image": "De-identified derivative",
    "summaryTitle": "Evidence summary",
    "generatedAt": "Report printed",
    "revisions": "Model revisions",
    "none": "—",
    "absent": "No model signal: {{findings}}.",
    "missing": "A dash means no evidence was returned for that cell; it does not mean absent. Scores are not percentages or calibrated probabilities."
  },
  "review": {
    "title": "4 · Reviewer feedback",
    "subtitle": "Evaluate the model findings and the summary. Your name is recorded as the reviewer.",
    "waiting": "Review opens when the analysis results are ready.",
    "findingsTitle": "Findings evaluation",
    "colModels": "Model results",
    "colAssessment": "Assessment",
    "colComment": "Comment (why incorrect, additions…)",
    "correct": "Correct",
    "incorrect": "Incorrect",
    "summaryTitle": "Summary evaluation",
    "overallTitle": "Overall notes / additions",
    "overallPlaceholder": "Anything the models missed, quality notes, next steps…",
    "commentPlaceholder": "Optional comment",
    "submit": "Save review",
    "submitting": "Saving…",
    "reviewsTitle": "Reviews",
    "you": "you",
    "noAssessments": "No per-finding assessments",
    "summaryLabel": "Summary",
    "agree": "Agree",
    "disagree": "Disagree",
    "colAgreement": "Agrees with verdict",
    "recordedContext": "Review the recorded MedSigLIP and MedGemma results. Synthetic comparison columns do not affect the conclusion."
  },
  "draft": {
    "withheld": "Withheld: this request has no accepted evidence for a findings draft. Loading the writer does not bypass the evidence or release gates.",
    "title": "MedGemma 27B opinion",
    "demo": "demo — release gate not passed",
    "claims": "{{count}} claims, each mapped to accepted facts",
    "note": "Text-only writer: it never sees the image, only the accepted structured facts. Unreviewed draft.",
    "generating": "MedGemma 27B is writing the draft…"
  },
  "evidence": {
    "title": "How to read this result",
    "simulated": "Simulated demonstration — this is not model performance.",
    "research": "Research evidence — not a clinical conclusion.",
    "source": "Public sample {{sample}} · {{source}}. Reference labels are dataset annotations, not a new clinical assessment.",
    "publicDataset": "public research dataset",
    "coverage": "Completed runs: {{completed}} / {{total}}.",
    "withheld": "Overall conclusion withheld. Individual outputs are not accepted findings.",
    "interpretation": "MedSigLIP values are uncalibrated ranking scores, not disease probabilities. MedGemma states are model observations. Missing evidence does not mean absence.",
    "next": "Human review: inspect the image, check each candidate finding and disagreement, and record corrections in Feedback. This demo does not recommend treatment.",
    "mockRun": "simulated",
    "reasons": {
      "thresholds_not_validated": "Thresholds have not been validated for the intended population; no accepted findings can be issued."
    },
    "runStatus": {
      "completed": "completed",
      "failed": "failed",
      "timed_out": "timed out",
      "abstained": "abstained",
      "queued": "queued",
      "running": "running"
    }
  },
  "demoGallery": {
    "eyebrow": "Research demo · public images",
    "title": "From image to reviewable evidence",
    "intro": "Choose a public adult frontal X-ray, run the models, and review each response with its provenance. You can also upload an original image from the Desktop folder using the upload tab.",
    "images": "{{count}} examples · adults · PA/AP",
    "originals": "Original pixels · separate references",
    "research": "Model output requires human review",
    "imageAlt": "Public frontal chest X-ray, example {{number}}",
    "case": "Example {{number}}",
    "reference": "Source reference",
    "referenceLimit": "Dataset annotation, not an expected model answer. Unmentioned labels are not negative findings.",
    "runs": "Latest case: {{completed}} / {{total}} real runs complete",
    "notRun": "Ready for its first run",
    "analyze": "Run analysis",
    "previous": "Open latest result",
    "measuredTitle": "Measured engineering improvement",
    "before": "Prompt only",
    "after": "Schema-constrained output",
    "measuredLimit": "On the same public image, the reader returned 1 of 9 required observations before schema constraints and 9 of 9 afterward. This measures structure, not diagnostic accuracy or weight fine-tuning. The app uses a 16-finding vocabulary.",
    "tuningTitle": "What tuning must still demonstrate",
    "tuningPlan": "Freeze the baseline and evaluation protocol. Tune on development data, then measure misses, false alerts and abstentions on separate patients and sites, with confidence intervals. Post-tuning clinical performance has not been measured.",
    "independence": "These studies overlap earlier selections and are not an independent benchmark.",
    "allSamples": "Browse all approved public samples",
    "context": {
      "noFinding": "No-finding source reference",
      "cardiac": "Cardiac silhouette reference",
      "opacity": "Opacity and edema reference",
      "multiple": "Multiple source findings",
      "pleuralAir": "Pleural air reference",
      "devices": "Complex film with devices",
      "tbReference": "TB-compatible appearance; not laboratory confirmation",
      "normalReference": "Normal source reference",
      "pneumoniaReference": "Pneumonia reference",
      "pleuralReference": "Pleural findings",
      "edemaReference": "Edema and consolidation",
      "tbCavityPleural": "TB-compatible reference · cavity / pleural thickening in source readings",
      "tbInfiltrateCalcification": "TB-compatible reference · infiltrate / calcification in source readings",
      "tbConsolidationApical": "TB-compatible reference · consolidation / apical changes in source readings",
      "tbCalcifiedNodes": "TB-compatible reference · calcified nodule and lymph node in source readings",
      "tbEffusionInfiltrate": "TB-compatible reference · pleural effusion and infiltrate in source readings"
    },
    "study": "Study {{number}}",
    "studyCount": "{{count}} studies"
  },
  "presets": {
    "eyebrow": "Instant research demo · No GPU needed",
    "title": "Eight ready-to-open presets",
    "intro": "Explore varied public chest X-rays with saved model responses. Open any preset instantly, or upload its matching image from the Desktop demo kit. These are recorded research results, not a new analysis.",
    "savedRuns": "{{count}} saved model results",
    "open": "Open study",
    "uploadHint": "Choose an original image from Desktop → MedAI Demo Kit → 01_upload. Its exact file contents open the corresponding saved report after a short loading sequence. No GPU or network inference is used; other files are rejected.",
    "banner": "Offline presets: recorded public-fixture research results. Opening a report performs no new model inference. Human review is required.",
    "recorded": "Saved preset · No new inference. Model timings describe the original run. Original prompts and outputs are preserved; missing findings were not measured."
  },
  "golden": {
    "normal": "Normal reference",
    "matched": "Reader finding matches the dataset reference",
    "normalNote": "Normal source reference; reader reports no finding",
    "open": "View saved report",
    "limit": "Selected examples illustrate individual finding matches, not accuracy across diseases. Full responses, contradictions and withheld conclusions remain visible in each report.",
    "library": "All 8 presets · Nine-model comparison · Additional reference cases",
    "reportTitle": "Recorded findings matching the reference",
    "reportNote": "These findings were reported by the saved MedGemma reader and occur in the public dataset reference. This is a per-finding comparison, not an accepted diagnosis.",
    "contradiction": "The original reader also reported “no finding” alongside positive findings. That contradiction is retained and requires review."
  },
  "findings": {
    "atelectasis": "Atelectasis",
    "cardiomegaly": "Cardiomegaly",
    "consolidation": "Consolidation",
    "edema": "Edema",
    "enlarged_cardiomediastinum": "Enlarged cardiomediastinum",
    "fracture": "Fracture",
    "hernia": "Hernia",
    "lung_lesion": "Lung lesion",
    "lung_opacity": "Lung opacity",
    "no_finding": "No finding",
    "pleural_effusion": "Pleural effusion",
    "pleural_other": "Pleural other",
    "pneumonia": "Pneumonia",
    "pneumothorax": "Pneumothorax",
    "support_devices": "Support devices",
    "pulmonary_tb_suspicion": "Pulmonary TB suspicion"
  },
  "comparison": {
    "datasetComparisonTitle": "Model results and dataset reference",
    "title": "Model results and doctor’s reference",
    "requestScope": "Request selection",
    "predefined": "Saved studies",
    "allRequests": "All requests",
    "predefinedHint": "Open a study to compare recorded model results with the source reference labels.",
    "savedResults": "Saved study · recorded results",
    "radiologistTitle": "Doctor’s reference",
    "datasetTitle": "Dataset reference",
    "sourceLabel": "Original source labels",
    "radiologistNote": "Radiologist image-reference labels supplied with the source dataset. They are independent of the model results and are not a new review submitted in this app.",
    "datasetNote": "Shenzhen supplies a TB-compatible appearance label, not a confirmed TB diagnosis or a full radiologist verdict for every finding.",
    "labelNote": "Not labeled means the source provides no reference for that finding. Model scores are ranking scores, not disease probabilities. These selected cases are not an accuracy benchmark.",
    "notLabeled": "Not labeled",
    "notAssessed": "Not assessed",
    "withheld": "Withheld",
    "readerAnnotations": "Accompanying findings in the source readings",
    "sourceReader": "Source reader",
    "annotatedFindings": "Annotated findings",
    "readerNumber": "Reader {{number}}",
    "readerAnnotationNote": "Each reader’s original annotations are shown separately. They are not consensus labels or additional confirmed disease diagnoses. An unlisted finding is not a negative finding.",
    "synthetic": "Synthetic",
    "syntheticValue": "Illustrative values",
    "syntheticNote": "Columns marked “Synthetic” contain generated examples, not model outputs.",
    "scrollModels": "Scroll horizontally to compare all models.",
    "featured": "Analyzed requests",
    "featuredHint": "Three selected studies. The complete library is available under Saved studies.",
    "highlightLegend": "Highlights show noted findings and the three highest scores within each column.",
    "topRanked": "One of this column’s three highest scores; not a clinical cutoff."
  },
  "playback": {
    "launch": "Launch analysis",
    "replay": "Replay analysis",
    "loading": "Loading results…",
    "recorded": "Recorded analysis",
    "title": "Loading the study results",
    "note": "Replaying saved results. Models are not run again.",
    "stages": {
      "prepare": "Preparing the study",
      "table": "Loading model results and comparison table",
      "report": "Loading the conclusion and source references"
    }
  },
  "overview": {
    "title": "Chest X-ray and MedGemma conclusion",
    "chestXray": "Chest X-ray",
    "saved": "Saved results",
    "verdict": "MedGemma conclusion",
    "pending": "Waiting for the model response.",
    "unavailable": "No completed MedGemma reading is available.",
    "withheld": "Overall conclusion withheld. These model observations require physician review.",
    "review": "Research model output. Physician review is required.",
    "notes": "Conclusion details and interpretation notes"
  }
}
